import { Controller, Loader } from '@tencent/workbench';
import { ChartFormItemKey } from '@tencent/chart-drawer-components/lib/schema';
import { AngleFrom, ColorOperations, DragAngleCircleType, NumberInputFrom, Form } from '@tencent/wb-ext-form';
import type { ColorFrom, IDynamicLoader } from '@tencent/workbench';
import { LINEAR_GRADIENT, RADIAL_GRADIENT, SHAPE_GRADIENT } from '@framework/workbench/drawer/ui/text/fill/config';
import { IDirection } from '@framework/workbench/drawer/ui/text/fill/fill-interface';
import { alphaToOpacity } from '@src/framework/workbench/drawer/utils';
import { IMultiDiffValue, INPUT_PROPS_CALLBACK_KEY } from '@src/framework/workbench/drawer/constants';
import { ANGLE_MAX } from '@src/framework/workbench/drawer/common/config';
import { BorderTypes, WIDTH_MAX, FORM_CONFIGS } from '@framework/workbench/drawer/ui/chart/global-settings/chart-line/config';
import { IChartGlobalLineFeature } from './chart-line-global-interface';

@Controller({ formItems: FORM_CONFIGS })
export class ChartGlobalLineController {
    constructor(@Loader(IChartGlobalLineFeature) private loader: IDynamicLoader<IChartGlobalLineFeature>) {}

    private get feature() {
        return this.loader.instance!;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_SELECT, 'value')
    public getBorderSelectValue(): string {
        this.feature.updateUIWithBorderData();
        return this.feature.curBorderType;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_SELECT, 'onChange')
    public changeBorderSelectValue(value: string) {
        this.feature.handleBorderTypeChange(value);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_SELECT, 'options')
    public getOptions() {
        return {
            selectOptions: BorderTypes,
        };
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE, 'content')
    public renderBorderLine() {
        return this.feature.renderBorderLine();
    }
    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_COLOR, 'content')
    public getColorSelectButton(): JSX.Element {
        return this.feature.getColorSelectButton();
    }


    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_WIDTH, 'value')
    public getWidth(): number | IMultiDiffValue {
        const widthObj = { width: this.feature.width };
        return this.feature.getShownValue(this.feature.getInputParams().key, widthObj) ?? 0.75;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_WIDTH, 'onChange')
    public changeWidth(value: number, from: NumberInputFrom): void {
        const { width } = this.feature.getValidValue({ width: value }, from, WIDTH_MAX);
        this.feature.handleBorderWidthChange(width, from);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_OPACITY, 'value')
    public getOpacity(): number | IMultiDiffValue {
        const opacityObj = { opacity: this.feature.opacity };
        return this.feature.getShownValue(this.feature.getInputParams().key, opacityObj) || 0;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_OPACITY, 'onChange')
    public changeOpacity(value: number, dragEnd?: boolean, from?: NumberInputFrom): void {
        const { opacity } = this.feature.getValidValue({ opacity: value }, from);
        this.feature.handleOpacityChange(opacity, dragEnd, from);
    }

    // 渐变填充 - 线性|径向
    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_LINEAR_GRADIENT, 'value')
    public getGradualFillLinear(): boolean {
        return this.feature.borderGradualValues.curGradualType === LINEAR_GRADIENT;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_LINEAR_GRADIENT, 'onClick')
    public changeGradualFillLinear() {
        this.feature.handleGradualTypeChange(LINEAR_GRADIENT);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_RADIAL_GRADIENT, 'value')
    public getGradualFillRadial(): boolean {
        return this.feature.borderGradualValues.curGradualType === RADIAL_GRADIENT;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_RADIAL_GRADIENT, 'onClick')
    public changeGradualFillRadial() {
        this.feature.handleGradualTypeChange(RADIAL_GRADIENT);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_ANGLE, 'value')
    public getGradualFillAngle(): number | IMultiDiffValue {
        const curAngleObj = { curAngle: this.feature.borderGradualValues.curAngle! };
        return this.feature.getShownValue(this.feature.getInputParams().key, curAngleObj);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_ANGLE, 'onChange')
    public changeGradualFillAngle(value: number, from: AngleFrom, dragState?: DragAngleCircleType) {
        const { curAngle } = this.feature.getValidValue({ curAngle: value }, from, ANGLE_MAX);
        const dragEnd = !dragState || dragState === DragAngleCircleType.STOP;
        this.feature.handleGradualAngleChange(curAngle % 360, from, dragEnd);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_DIRECTION, 'value')
    public getGradualFillDirect(): IDirection {
        return this.feature.borderGradualValues.curDirect!;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_DIRECTION, 'onChange')
    public changeGradualFillDirect(value: IDirection) {
        this.feature.handleGradualDirectionChange(value);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_DIRECTION, 'options')
    public getGradualFillDirectionOptions() {
        return {
            selectOptions: this.feature.getFillDirectionOptions(),
        };
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_BAR, 'content')
    public renderGradualFill() {
        return this.feature.renderGradualFill();
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_OPACITY, 'value')
    public getGradualFillOpacity(): number | IMultiDiffValue {
        const color = this.feature.borderGradualValues.activeColor;
        if (!color) {
            return 0;
        }
        const gradualOpacityObj = { gradualOpacity: alphaToOpacity(color.alpha) };
        return this.feature.getShownValue(this.feature.getInputParams().key, gradualOpacityObj) || 0;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_OPACITY, 'onChange')
    public changeGradualFillOpacity(value: number, dragEnd?: boolean, from?: NumberInputFrom) {
        const { gradualOpacity } = this.feature.getValidValue({ gradualOpacity: value }, from);
        this.feature.handleGradualOpacityChange(gradualOpacity, from, dragEnd);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_ANGLE_TEXT, 'shouldDisabled')
    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_ANGLE, 'shouldDisabled')
    public shouldAngleDisabled(): boolean {
        return this.feature.shouldAngleDisabled();
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_DIRECTION, 'shouldDisabled')
    @Form(ChartFormItemKey.CHART_GLOBAL_LINE_GRADUAL_DIRECTION_TEXT, 'shouldDisabled')
    public shouldDirectionDisabled(): boolean {
        return this.feature.borderGradualValues.curGradualType === SHAPE_GRADIENT;
    }
}
